import { defineComponent as _defineComponent } from 'vue'
import { ref, toRefs, computed, useContext, onMounted, onUnmounted, watch } from "@nuxtjs/composition-api";
import { fromWei, toBN, toCurrencyFormat, toNumericFormatBTC, toWei } from "~/core/helpers/GlobalHelpers";
import { useMobile } from "~/core/composables/useMobile";
import { MetaWorldManager } from "@/core/services/map/MetaWorldManager";
import { MetaInventoryContainer } from "~/core/models/MetaInventoryContainer";
import { SubscriptionService } from "~/pages/nfts/products/services/SubscriptionService";
import { ACTIONS as ACTIONS_USER_FEEDBACK } from "~/store/affility/user/feedback/names";
import { ACTIONS as USER_FEEDBACK_ACTIONS } from "~/store/affility/user/feedback/modals/full-loader/names";
import { ACTIONS as TOAST_ACTIONS } from "~/store/affility/user/feedback/modals/toast-notification/names";
import { ACTIONS as AUTH_ACTIONS } from "~/store/affility/user/auth/names";
import { WALLET_ERROR_CODES } from "~/core/services/utils/Constants";
import Asset from "@/pages/finances/wallet/models/Asset";
import BNBService from "~/pages/finances/wallet/services/BNBService";

interface Props {
    purchaseData: any,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'PurchaseCore',
  props: {
    purchaseData: null
  },
  emits: ['onFlowFinished'],
  setup(__props: any, { emit: $emit }) {

const props = __props as { purchaseData: any };

const GAS_FEE_MULTIPLIER_FOR_INTERNAL = 1.3;





const {
    purchaseData,
} = toRefs(props);

const isMobile = useMobile();
const { store, $readWeb3, $web3, i18n } = useContext();

const isLoading = ref(true);
const isSubmitLoading = ref(false);
const txObjectInternal = ref(null);
const bnbIntervalTimer = ref(null);
const internalGasData = ref({ amount: 0, usd: 0 });
const isEnabled = ref(false);
const isEstimatingGasFees = ref(false);
const userBnbBalance = ref(0);

const user = computed(() => store.getters['application/driver/authUser']);
const contracts = computed(() => MetaWorldManager.sharedInstance().contracts);
const priceData = computed(() => {
    return new Asset(
        null,
        "Tether USD",
        'USDT',
        require('~/assets/images/wallets/usdt.png'),
        Number(purchaseData.value.amount),
        1,
        false,
        MetaWorldManager.sharedInstance(),
        "Binance Smart Chain (BEP20)",
    )
});
const getUserCryptoBalance = computed(() => {
    return MetaInventoryContainer.sharedInstance().userCryptoBalance.usdt ?? 0;
});
const hasEnoughBNBFeeBalance = computed(() => {
    return userBnbBalance.value && userBnbBalance.value >= internalGasData.value.amount;
});
const hasEnoughUSDTFeeBalance = computed(() => {
    return getUserCryptoBalance.value >= purchaseData.value.amount;
});
const isActionDisabled = computed(() => {
    return (
        user.value?.isFullyWeb2User &&
        isEnabled.value &&
        (
            !hasEnoughBNBFeeBalance.value ||
            !hasEnoughUSDTFeeBalance.value ||
            isEstimatingGasFees.value
        )
    ) || (
        !user.value?.isFullyWeb2User && isEnabled.value && !hasEnoughUSDTFeeBalance.value
    )
});

const openFullScreenPopup = () => {
    store.dispatch(`affility/user/feedback/modals/full-loader/${USER_FEEDBACK_ACTIONS.OPEN_FULL_PAGE_LOADING}`);
}

const closeFullScreenPopup = () => {
    store.dispatch(`affility/user/feedback/modals/full-loader/${USER_FEEDBACK_ACTIONS.CLOSE_FULL_PAGE_LOADING}`);
}

const getWeb3Allowance = async () => {
    let allowance = 0;
    const walletAddress = user.value?.walletAddress;
    if (walletAddress) {
        const autoshipContract = MetaWorldManager.sharedInstance().contracts;
        const autoshipContractAllowance = autoshipContract.USDT.methods.allowance;
        const autoshipAddress = autoshipContract.BinarySubscription._address
        allowance = Number(fromWei(
            await autoshipContractAllowance(walletAddress, autoshipAddress).call(),
        ));
    }

    return allowance;
}

const estimateGasFees = async () => {
    isEstimatingGasFees.value = true;
    try {
        const wallet = user.value.walletAddress;
        const args = [wallet, toWei(String(purchaseData.value.amount)), purchaseData.value.months];
        const transaction = contracts.value.BinarySubscription.methods.purchaseSubscription(args[0], args[1], args[2]);
        const gasWithoutMultiplier = await transaction.estimateGas({ from: wallet });
        const GASEstimated =  toBN(Math.ceil(gasWithoutMultiplier * GAS_FEE_MULTIPLIER_FOR_INTERNAL));
        const gasPrice = toBN(await $readWeb3().eth.getGasPrice());
        const totalAmountWithGas = GASEstimated.mul(gasPrice);
        const GasEstimatedInBNB = $readWeb3().utils.fromWei(totalAmountWithGas.toString());
        const bnbPriceInETH = await contracts.value.GymNetwork.methods.getBNBPrice().call();
        internalGasData.value = {
            amount: GasEstimatedInBNB,
            usd: parseFloat(GasEstimatedInBNB) * bnbPriceInETH
        }
        txObjectInternal.value = {
            to: contracts.value.BinarySubscription._address,
            value: 0,
            data: transaction.encodeABI(),
        }
    } finally {
        isEstimatingGasFees.value = false;
    }
}

const getBinaryUsers = async () => {
    try {
        const users = await SubscriptionService.getBinaryUsers();
        await store.dispatch(`affility/user/auth/${AUTH_ACTIONS.UPDATE_USER_AUTH_DATA}`, {
            currentRankName: users?.user?.currentRank,
            commissionRankName: users?.user?.commissionRank,
        });
    } catch (e) {
        console.log(e);
    }
}

const checkAllowance = async () => {
    await getBinaryUsers();

    if (user.value?.isFullyWeb2User) {
        isLoading.value = true;
        try {
            const allowance = await store.dispatch('application/driver/checkAllowance', {
                tokenAddress: contracts.value.USDT._address,
                spenderAddress: contracts.value.BinarySubscription._address,
            });
            isEnabled.value = !!allowance;

            await estimateGasFees();
        } finally {
            isLoading.value = false;
        }
    } else {
        try {
            const allowanceWeb3 = await getWeb3Allowance();
            isEnabled.value = allowanceWeb3 >= purchaseData.value.amount;
        } finally {
            isLoading.value = false;
        }
    }
}

const showError = (error: any) => {
    const code = error?.code;
    const message = error?.message;
    let text: any;

    if (code === WALLET_ERROR_CODES.USER_REJECTED ||
        code === WALLET_ERROR_CODES.REJECTED_BY_NETWORK ||
        message.includes('rejected')) {
        text = i18n.t('Your transaction has been declined. Please attempt the transaction again.');
    } else if (code === WALLET_ERROR_CODES.METAMASK_INTERNAL_ERROR ||
        message.includes('nonce mismatch') || message.includes('nonce too low')) {
        text = i18n.t('Please wait for the ongoing transaction on the blockchain to complete before initiating a new one.');
    } else if (message.includes('insufficient funds for gas * price + value')) {
        text = i18n.t('Your transaction has been declined. Please add BNB to your wallet and attempt the transaction again.');
    } else if (message.includes('reverted by EVM') || message.includes('reverted')) {
        text = i18n.t('Blockchain Notification: Apologies, an error occurred on the blockchain side. Please try again.');
    } else {
        text = i18n.t('Apologies, an unexpected error occurred. Please retry your request. If the issue persists, please contact support.');
    }

    store.dispatch(`affility/user/feedback/modals/toast-notification/${TOAST_ACTIONS.SHOW_ERROR_TOAST}`, {
        description: text,
    })
}

const handleApproveAllowance = async () => {
    if (user.value?.isFullyWeb2User) {
        isSubmitLoading.value = true;
        try {
            const allowanceAmount = Number(1e24).toLocaleString('fullwide', { useGrouping: false });
            const transactionObject = {
                to: contracts.value.USDT._address,
                value: 0,
                data: $web3().eth.abi.encodeFunctionCall({
                    name: "approve",
                    inputs: [
                        {
                            internalType: "address",
                            name: "spender",
                            type: "address",
                        },
                        {
                            internalType: "uint256",
                            name: "amount",
                            type: "uint256",
                        },
                    ],
                    type: "function",
                }, [contracts.value.BinarySubscription._address, allowanceAmount]),
            };

            await store.dispatch('application/driver/internalWalletSendTx', transactionObject);
            isEnabled.value = true;

            if (!internalGasData.value?.amount && hasEnoughUSDTFeeBalance.value) {
                await estimateGasFees();
            }
        } catch (error) {
            showError(error);
            console.log(error);
        } finally {
            isSubmitLoading.value = false;
        }
    } else {
        openFullScreenPopup();
        try {
            await store.dispatch("user/subscription/approveAllowance", purchaseData.value.amount);
            isEnabled.value = true;
        } catch (error) {
            showError(error);
            console.log(error);
        } finally {
            closeFullScreenPopup();
        }
    }
}

const setSuccessAndAutoshipUpdated = () => {
    store.dispatch('user/subscription/updateUserSubscriptionExpires');
    store.dispatch('user/subscription/updateFrozenAutoship');
    $emit('onFlowFinished');
    store.dispatch(
        `affility/user/feedback/${ACTIONS_USER_FEEDBACK.SHOW_SUCCESS_MODAL}`,
        { title: i18n.t('Purchase Successful') }
    );
}

const handleBuyAutoship = async () => {
    if (user.value?.isFullyWeb2User) {
        isSubmitLoading.value = true;
        try {
            await store.dispatch('application/driver/internalWalletSendTx', txObjectInternal.value);

            setSuccessAndAutoshipUpdated();
        } catch (error) {
            showError(error);
            console.log(error);
        } finally {
            isSubmitLoading.value = false;
        }
    } else {
        openFullScreenPopup();
        try {
            await store.dispatch("user/subscription/buySubscription", {
                amount: purchaseData.value.amount,
                months: purchaseData.value.months
            });

            setSuccessAndAutoshipUpdated();
        } catch (error) {
            showError(error);
            console.log(error);
        } finally {
            closeFullScreenPopup();
        }
    }
}

const getBnbBalance = async () => {
    const res: any = await BNBService.getBalance(user.value.walletAddress, MetaWorldManager.sharedInstance());
    userBnbBalance.value = parseFloat(res);
}

watch(getUserCryptoBalance, (newVal, oldValue) => {
    if (oldValue <= purchaseData.value.amount && newVal >= purchaseData.value.amount) {
        estimateGasFees();
    }
})

onMounted(() => {
    getBnbBalance();
    bnbIntervalTimer.value = setInterval (async () => {
        getBnbBalance();
    }, 5000);
})

onUnmounted(() => {
    clearInterval(bnbIntervalTimer.value);
})

checkAllowance();

return { __sfc: true,GAS_FEE_MULTIPLIER_FOR_INTERNAL, $emit, props, purchaseData, isMobile, store, $readWeb3, $web3, i18n, isLoading, isSubmitLoading, txObjectInternal, bnbIntervalTimer, internalGasData, isEnabled, isEstimatingGasFees, userBnbBalance, user, contracts, priceData, getUserCryptoBalance, hasEnoughBNBFeeBalance, hasEnoughUSDTFeeBalance, isActionDisabled, openFullScreenPopup, closeFullScreenPopup, getWeb3Allowance, estimateGasFees, getBinaryUsers, checkAllowance, showError, handleApproveAllowance, setSuccessAndAutoshipUpdated, handleBuyAutoship, getBnbBalance, toCurrencyFormat, toNumericFormatBTC }
}

})